import type { BlueprintMessage, SeedsMapEntry } from "@/types/creativesphere";

/** How each seed input carries into the propagated collection. */
export type SeedDisposition = "changes" | "locked";

export interface SeedRow {
  label: string;
  value: string;
  disposition: SeedDisposition;
}

export function describeSeedEntry(
  entry: SeedsMapEntry | undefined,
): string | undefined {
  if (!entry) return undefined;
  const name = entry.name?.trim();
  if (name) return name;
  const slug = entry.slug?.trim();
  if (!slug) return undefined;
  const words = slug.replace(/[_-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The seed summary rows for the Accept-the-pilot modal.
 * Product names the pilot's product (it swaps per SKU); Composition/Copy/Brand
 * voice are the same for every pilot; Scene and Photography are filled in from
 * the chosen blueprint's seeds_map, falling back to "Brand default" when the
 * blueprint doesn't carry that seed.
 */
export function buildSeedRows(
  blueprint?: BlueprintMessage,
  productName?: string,
): SeedRow[] {
  const seedsMap = blueprint?.seeds_map;
  const scene = describeSeedEntry(seedsMap?.scene) ?? "Brand default";
  const photography =
    describeSeedEntry(seedsMap?.photography) ?? "Brand default";
  return [
    {
      label: "Product",
      value: productName?.trim() || "Swapped per SKU",
      disposition: "changes",
    },
    { label: "Scene", value: scene, disposition: "locked" },
    { label: "Photography", value: photography, disposition: "locked" },
    {
      label: "Composition",
      value: "From the blueprint",
      disposition: "locked",
    },
    {
      label: "Copy",
      value: "Re-resolved from each product's attributes",
      disposition: "changes",
    },
    {
      label: "Brand voice",
      value: "From the brand book",
      disposition: "locked",
    },
  ];
}
